import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  MenuItem,
  useTheme,
  Dialog,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Assignment as ProjectIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  PriorityHigh as PriorityIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Label as LabelIcon,
  EditNote as EditNoteIcon,
  PlayCircle as PlayCircleIcon,
  PauseCircle as PauseCircleIcon,
  Close as CloseIcon,
  LocalOffer as LocalOfferIcon,
  Visibility as VisibilityIcon,
  Folder as FolderIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
} from "@mui/icons-material";
import {
  fetchProjects,
  deleteExistingProject,
} from "store/features/company/projectManagement/project/projectActions";
import {
  selectProjects,
  selectProjectLoading,
  selectProjectDeleting,
} from "store/features/company/projectManagement/project/projectSelectors";
import { fetchDepartments } from "store/features/company/department/departmentActions";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import AddProjectModal from "./AddProjectModal";
import EditProjectModal from "./EditProjectModal";
import projectSvg from "assets/folders/project.svg";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import usePermissions from "hooks/usePermissions";

const Project = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const rawProjects = useSelector(selectProjects);
  const loading = useSelector(selectProjectLoading);
  const departments = useSelector(selectDepartments);
  const employees = useSelector(selectEmployees);
  const currentUser = useSelector(selectCurrentUser);
  const role = getSecureLocalItem("role") || "unknown";
  const userRaw = getSecureLocalItem("user");
  const storedUser = userRaw
    ? typeof userRaw === "string"
      ? JSON.parse(userRaw)
      : userRaw
    : null;
  const user = currentUser || storedUser;

  const currentUserId = user?.id || user?.user_id;
  const currentEmployee = employees.find(
    (emp) =>
      (user?.employee_id && String(emp.id) === String(user.employee_id)) ||
      (user?.id && String(emp.user_id) === String(user.id)) ||
      (user?.id && String(emp.id) === String(user.id)) ||
      (user?.email &&
        emp.email &&
        emp.email.toLowerCase() === user.email.toLowerCase()),
  );
  const currentEmployeeId =
    currentEmployee?.id || user?.employee_id || user?.id;

  const projects = useMemo(() => {
    if (!rawProjects || !Array.isArray(rawProjects)) return [];
    if (role === "Company") return rawProjects;
    if (!currentUserId && !currentEmployeeId) return rawProjects;

    return rawProjects.filter((project) => {
      // 1. Is Project Lead
      const managerId = project.project_manager_id;
      const isProjectLead =
        managerId !== undefined &&
        managerId !== null &&
        ((currentEmployeeId &&
          String(managerId) === String(currentEmployeeId)) ||
          (currentUserId && String(managerId) === String(currentUserId)));

      // 2. Is Team Member
      const teamMembers =
        project.team_members || project.teamMembers || project.members || [];

      const isTeamMember =
        Array.isArray(teamMembers) &&
        teamMembers.some(
          (memberId) =>
            (currentEmployeeId &&
              String(memberId) === String(currentEmployeeId)) ||
            (currentUserId && String(memberId) === String(currentUserId)),
        );

      return isProjectLead || isTeamMember;
    });
  }, [rawProjects, currentUserId, currentEmployeeId, role]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [projectLeadFilter, setProjectLeadFilter] = useState("all");
  const [teamMembersDialogOpen, setTeamMembersDialogOpen] = useState(false);
  const [selectedProjectForTeam, setSelectedProjectForTeam] = useState(null);
  const { hasAnyPermission, hasAllPermissions, hasPermission } =
    usePermissions();

  // const role = getSecureLocalItem("role") || "unknown";
  const navigate = useNavigate();
  useEffect(() => {
    if (role == "Employee") {
      dispatch(fetchProjects("sort"));
    } else if (role === "Company") {
      dispatch(fetchProjects());
    }
    dispatch(fetchDepartments());
    dispatch(fetchEmployees());
  }, [dispatch, role]);

  const employeeNameMap = {};
  employees.forEach((emp) => {
    employeeNameMap[emp.id] = `${emp.first_name} ${emp.last_name}`;
  });

  const departmentMap = {};
  departments.forEach((dept) => {
    departmentMap[dept.id] = dept.name;
  });

  const employeeMap = {};
  employees.forEach((emp) => {
    employeeMap[emp.id] = `${emp.first_name} ${emp.last_name}`;
  });

  const handleViewTeamMembers = (project) => {
    setSelectedProjectForTeam(project);
    setTeamMembersDialogOpen(true);
  };

  const handleCloseTeamMembers = () => {
    setTeamMembersDialogOpen(false);
    setSelectedProjectForTeam(null);
  };

  const sortedProjects = [...projects].sort((a, b) => b.id - a.id);
  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (projectToDelete) {
      setDeleting(true);
      const result = await dispatch(deleteExistingProject(projectToDelete.id));
      if (result.success) {
        dispatch(fetchProjects());
        setDeleteDialogOpen(false);
        setProjectToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  const handleProject = (project) => {
    navigate(`/company/projects/${project.id}`, { state: { project } });
  };

  // Cursor-following glow for the clickable project navigation boxes.
  // Tracks pointer position via CSS variables so the radial glow moves with the cursor.
  const handleNavigateGlow = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty(
      "--mouse-x",
      `${e.clientX - rect.left}px`,
    );
    e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
  };

  const navigateGlowSx = {
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background:
        theme.palette.mode === "dark"
          ? `
      radial-gradient( 340px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(73, 71, 233, 0.4) 0%, rgba(77, 80, 255, 0.26) 20%, rgba(153, 45, 255, 0.15) 40%, rgba(98, 0, 255, 0.08) 60%, transparent 90% )
    `
          : `
      radial-gradient(
        420px circle at var(--mouse-x,50%) var(--mouse-y,50%),
        rgba(255,205,95,.35) 0%,
        rgba(255,184,77,.24) 20%,
        rgba(255,160,45,.14) 42%,
        rgba(255,140,0,.08) 62%,
        transparent 90%
      )
    `,
      opacity: 0,
      transition: "opacity 0.3s ease",
      pointerEvents: "none",
      zIndex: 0,
    },
    "&:hover::before": {
      opacity: 1,
    },
    // keep the real content above the glow layer
    "& > *": {
      position: "relative",
      zIndex: 1,
    },
  };

  const filteredProjects = sortedProjects.filter((project) => {
    const matchesSearch =
      searchTerm === "" ||
      project.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || project.status === statusFilter;

    const matchesPriority =
      priorityFilter === "all" || project.priority === priorityFilter;

    const matchesDepartment =
      departmentFilter === "all" ||
      project.department_id === parseInt(departmentFilter);

    const matchesProjectLead =
      projectLeadFilter === "all" ||
      project.project_manager_id === parseInt(projectLeadFilter);

    return (
      matchesSearch &&
      matchesStatus &&
      matchesPriority &&
      matchesDepartment &&
      matchesProjectLead
    );
  });

  const formatDateShort = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    return `${day}/${month}/${String(year).slice(-2)}`;
  };

  const formatBudget = (budget) => {
    if (!budget) return "₹0";
    const num = parseFloat(budget);
    if (isNaN(num)) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return { bg: "#d1fae5", color: "#065f46" };
      case "inactive":
        return { bg: "#fee2e2", color: "#991b1b" };
      case "completed":
        return { bg: "#dbeafe", color: "#1e40af" };
      case "on-hold":
        return { bg: "#fef3c7", color: "#92400e" };
      default:
        return { bg: "#e5e7eb", color: "#374151" };
    }
  };

  const getAssignmentTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "email":
        return { bg: "#dbeafe", color: "#1e40af" };
      case "written":
        return { bg: "#ede9fe", color: "#5b21b6" };
      case "verbal":
        return { bg: "#fef3c7", color: "#92400e" };
      case "meeting resolutions":
        return { bg: "#d1fae5", color: "#065f46" };
      case "regular assignments":
        return { bg: "#e0f2fe", color: "#075985" };
      case "self":
        return { bg: "#fce7f3", color: "#9d174d" };
      default:
        return { bg: "#e5e7eb", color: "#374151" };
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={projectSvg}
              alt="Projects"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Projects
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage your organization projects
              </Typography>
            </Box>
          </Stack>

          <Box>
            {hasPermission("projects.create") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add Project
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems={{ lg: "center" }}
        sx={{ mb: 3, flexWrap: "wrap" }}
      >
        <TextField
          placeholder="Search by project name, code or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 2,
            minWidth: { xs: "100%", sm: 250 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Department"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 180 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "primary.main",
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  bgcolor: "background.paper",
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Departments</MenuItem>
          {departments.map((dept) => (
            <MenuItem key={dept.id} value={dept.id}>
              {dept.name} ({dept.code})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Project Lead"
          value={projectLeadFilter}
          onChange={(e) => setProjectLeadFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 180 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "primary.main",
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  bgcolor: "background.paper",
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Project Leads</MenuItem>
          {employees.map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              {emp.first_name} {emp.last_name} ({emp.employee_code})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 150 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "primary.main",
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  bgcolor: "background.paper",
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="planning">Planning</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="on_hold">On Hold</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>

        <TextField
          select
          label="Priority"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 150 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "primary.main",
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  bgcolor: "background.paper",
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Priorities</MenuItem>
          <MenuItem value="urgent">Urgent</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="low">Low</MenuItem>
        </TextField>

        {(departmentFilter !== "all" ||
          projectLeadFilter !== "all" ||
          statusFilter !== "all" ||
          priorityFilter !== "all" ||
          searchTerm !== "") && (
          <Button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setPriorityFilter("all");
              setDepartmentFilter("all");
              setProjectLeadFilter("all");
            }}
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              color: "primary.main",
              borderColor: "#f59e0b",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#d97706",
                backgroundColor: "rgba(245,158,11,0.1)",
              },
            }}
            variant="outlined"
          >
            Clear All Filters
          </Button>
        )}
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading projects...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredProjects.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                borderRadius: "16px",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
              }}
            >
              <ProjectIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                No projects found. Click "Add Project" to get started.
              </Typography>
            </Box>
          ) : (
            filteredProjects.map((project, index) => {
              const priorityColor =
                project.priority === "urgent"
                  ? theme.palette.mode === "dark"
                    ? "#f472b6"
                    : "#db2777"
                  : project.priority === "high"
                  ? theme.palette.mode === "dark"
                    ? "#fb3c3c"
                    : "#ea0c0c"
                  : project.priority === "medium"
                  ? theme.palette.mode === "dark"
                    ? "#fbbf24"
                    : "#d97706"
                  : project.priority === "low"
                  ? theme.palette.mode === "dark"
                    ? "#4ade80"
                    : "#16a34a"
                  : theme.palette.mode === "dark"
                  ? "#9ca3af"
                  : "#6b7280";
              return (
                <Box
                  key={project.id}
                  component={motion.div}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#111827" : "#ffffff",
                    border: `1px solid ${
                      theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                    }`,
                    borderLeft: `4px solid ${priorityColor}`,
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 4px 12px -2px rgba(0,0,0,0.3)"
                        : "0 4px 12px -2px rgba(0,0,0,0.05)",
                    // "&:hover": {
                    //   backgroundColor:
                    //     theme.palette.mode === "dark"
                    //       ? "rgba(255,178,46,0.05)"
                    //       : "rgba(245,158,11,0.03)",
                    // },
                  }}
                >
                  <Box
                    onClick={() => handleProject(project)}
                    onMouseMove={handleNavigateGlow}
                    sx={{
                      display: "flex",
                      flex: "2 1 330px",
                      minWidth: 270,
                      alignItems: "stretch",
                      cursor: "pointer",

                      ...navigateGlowSx,

                      "& > *": {
                        position: "relative",
                        zIndex: 1,
                      },
                    }}
                  >
                    {/* ID / Tenant */}
                    <Box
                      sx={{
                        width: 130,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 2,
                        transition: "all .2s ease",
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#2a2d3c"
                              : "#f3f4f6",
                          borderRadius: "6px",
                          px: 1.25,
                          py: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "1rem",
                            fontWeight: 700,
                            color: priorityColor,
                          }}
                        >
                          #{project.id}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#6b7280"
                              : "#9ca3af",
                        }}
                      >
                        Tenant: {project.tenant_id}
                      </Typography>
                    </Box>

                    {/* Code / Name / Description / Priority / Department */}
                    <Box
                      sx={{
                        flex: 1,
                        p: 2,
                        minWidth: 140,
                        gap: 0.75,
                        display: "flex",
                        flexDirection: "column",
                        transition: "all .2s ease",
                        borderRight: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "primary.main",
                          mb: 0.5,
                        }}
                      >
                        {project.code}
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                          fontSize: "1rem",
                        }}
                      >
                        {project.name}
                      </Typography>

                      {project.description && (
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontStyle: "italic",
                            color: "text.secondary",
                            mb: 1,
                          }}
                        >
                          {project.description}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          flexWrap: "wrap",
                          mt: 1,
                        }}
                      >
                        <Chip
                          icon={
                            project.priority === "urgent" ? (
                              <WarningIcon sx={{ fontSize: 14 }} />
                            ) : project.priority === "high" ? (
                              <TrendingUpIcon sx={{ fontSize: 14 }} />
                            ) : project.priority === "medium" ? (
                              <TrendingFlatIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <TrendingDownIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          label={project.priority?.toUpperCase() || "N/A"}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: priorityColor,
                            color: priorityColor,
                            fontWeight: "bold",
                            fontStyle: "italic",
                            fontSize: "0.7rem",
                            px: 1,
                            height: "28px",
                          }}
                        />

                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <FolderIcon
                            sx={{ fontSize: 14, color: "primary.main" }}
                          />

                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: "bold",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {departmentMap[project.department_id] ||
                              `Department ${project.department_id}`}
                            {project.department_id &&
                              ` (ID ${project.department_id})`}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Project Lead / Timeline */}
                  <Box
                    sx={{
                      flex: "1 1 220px",
                      p: 2,
                      minWidth: 180,
                      borderRight: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Project Lead
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <PersonIcon
                        sx={{ fontSize: 16, color: "primary.main" }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: "bold",
                          fontStyle: "italic",
                          color:
                            theme.palette.mode === "dark"
                              ? "#d1d5db"
                              : "#4b5563",
                        }}
                      >
                        {employeeMap[project.project_manager_id] ||
                          `Employee ${project.project_manager_id}`}
                        {project.project_manager_id &&
                          ` (ID ${project.project_manager_id})`}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                      onClick={() => handleViewTeamMembers(project)}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        backgroundColor: "#f59e0b",
                        color: "#ffffff",
                        borderRadius: "8px",
                        padding: "4px 12px",
                        minWidth: "150px",
                        minHeight: "32px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        transition: "all 0.2s ease",
                        mb: 2,
                        "&:hover": {
                          backgroundColor: "#d97706",
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 8px rgba(245,158,11,0.3)",
                        },
                        "&:active": {
                          transform: "translateY(1px)",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      View Team
                    </Button>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Timeline
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EventIcon
                        sx={{
                          fontSize: 14,
                          color:
                            theme.palette.mode === "dark"
                              ? "#d1d5db"
                              : "#4b5563",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#d1d5db"
                              : "#4b5563",
                          fontFamily: "monospace",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDateShort(project.start_date)} -{" "}
                        {formatDateShort(project.end_date)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Status / Budget / Tags */}
                  <Box sx={{ flex: "1 1 200px", p: 2, minWidth: 180 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Status
                    </Typography>
                    <Chip
                      icon={
                        project.status === "planning" ? (
                          <EditNoteIcon sx={{ fontSize: 14 }} />
                        ) : project.status === "active" ? (
                          <RadioButtonCheckedIcon sx={{ fontSize: 14 }} />
                        ) : project.status === "on_hold" ? (
                          <RadioButtonCheckedIcon sx={{ fontSize: 14 }} />
                        ) : project.status === "completed" ? (
                          <CheckCircleIcon sx={{ fontSize: 14 }} />
                        ) : project.status === "cancelled" ? (
                          <CancelIcon sx={{ fontSize: 14 }} />
                        ) : undefined
                      }
                      label={
                        project.status === "on_hold"
                          ? "On Hold"
                          : project.status === "planning"
                          ? "Planning"
                          : project.status?.charAt(0).toUpperCase() +
                              project.status?.slice(1) || "N/A"
                      }
                      size="small"
                      sx={{
                        backgroundColor:
                          project.status === "planning"
                            ? "#fef3c7"
                            : project.status === "active"
                            ? "#d1fae5"
                            : project.status === "on_hold"
                            ? "#fef3c7"
                            : project.status === "completed"
                            ? "#dbeafe"
                            : project.status === "cancelled"
                            ? "#fee2e2"
                            : "#e5e7eb",
                        color:
                          project.status === "planning"
                            ? "#92400e"
                            : project.status === "active"
                            ? "#065f46"
                            : project.status === "on_hold"
                            ? "#92400e"
                            : project.status === "completed"
                            ? "#1e40af"
                            : project.status === "cancelled"
                            ? "#991b1b"
                            : "#374151",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        height: "24px",
                        mb: 1.5,
                        "& .MuiChip-icon": {
                          color: "inherit",
                          fontSize: "14px",
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        textTransform: "uppercase",
                        mt: 1.5,
                        mb: 0.5,
                      }}
                    >
                      Assignment Type
                    </Typography>
                    {project.assignment_type ? (
                      <Chip
                        label={project.assignment_type}
                        size="small"
                        sx={{
                          backgroundColor: getAssignmentTypeColor(
                            project.assignment_type,
                          ).bg,
                          color: getAssignmentTypeColor(project.assignment_type)
                            .color,
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          height: "24px",
                          mb: 1.5,
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#6b7280"
                              : "#9ca3af",
                          mb: 1.5,
                        }}
                      >
                        Not set
                      </Typography>
                    )}
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 4,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            color: "text.secondary",
                            textTransform: "uppercase",
                            mb: 0.5,
                          }}
                        >
                          Budget
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color:
                              theme.palette.mode === "dark"
                                ? "#fbbf24"
                                : "#d97706",
                            mb: 1.5,
                          }}
                        >
                          {formatBudget(project.budget)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            minWidth: 90,
                            display: "flex",
                            alignItems: "flex-start",
                            flexDirection: "column",
                            color: "text.secondary",
                            textTransform: "uppercase",
                            mb: 0.5,
                          }}
                        >
                          Tags
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "flex-start" }}
                        >
                          {project.tags && project.tags.length > 0 ? (
                            project.tags.map((tag, idx) => (
                              <Chip
                                key={idx}
                                label={tag}
                                size="small"
                                icon={<LocalOfferIcon />}
                                sx={{
                                  backgroundColor: "transparent",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#4bb0de"
                                      : "#0284c7",
                                  fontStyle: "italic",
                                  height: "22px",
                                  fontSize: "0.7rem",
                                  width: "fit-content",
                                  margin: "-2px 0",
                                  "& .MuiChip-icon": {
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#4bb0de"
                                        : "#0284c7",
                                    fontSize: "12px",
                                    margin: "0 2px 0 -4px",
                                  },
                                  "& .MuiChip-label": {
                                    fontStyle: "italic",
                                    padding: "0 4px",
                                  },
                                }}
                              />
                            ))
                          ) : (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#6b7280"
                                    : "#9ca3af",
                              }}
                            >
                              (No tags)
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </Box>

                  {/* Actions */}
                  {hasAnyPermission(["projects.update", "projects.delete"]) && (
                    <Box
                      sx={{
                        width: 90,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        borderLeft: "1px solid rgba(255,255,255,0.08)",
                        gap: 0.75,
                        justifyContent: "flex-start",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          textTransform: "uppercase",
                          mb: 0.5,
                        }}
                      >
                        Actions
                      </Typography>
                      {hasPermission("projects.update") && (
                        <IconButton
                          onClick={() => {
                            setSelectedProject(project);
                            setShowEditModal(true);
                          }}
                          size="small"
                          sx={{
                            color: "primary.main",
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(245,158,11,0.2)"
                                  : "#fff5ce",
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {hasAllPermissions([
                        "projects.update",
                        "projects.delete",
                      ]) && (
                        <Box
                          sx={{
                            width: "100%",
                            height: "1px",
                            backgroundColor: "rgba(255,255,255,0.08)",
                            my: 1,
                          }}
                        />
                      )}
                      {hasPermission("projects.delete") && (
                        <IconButton
                          onClick={() => handleDeleteClick(project)}
                          disabled={deleting}
                          size="small"
                          sx={{
                            color: "#ef4444",
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(239,68,68,0.2)"
                                  : "#fee2e2",
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Stack>
      )}
      {/* Project Count Summary */}
      {!loading && projects.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">
            Total Projects: {projects.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredProjects.length} of {projects.length}
          </Typography>
        </Stack>
      )}

      {/* Empty State for No Search Results */}
      {!loading && filteredProjects.length === 0 && projects.length > 0 && (
        <Paper
          sx={{
            mt: 4,
            p: 4,
            textAlign: "center",
            borderRadius: "16px",
            bgcolor: "background.paper",
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
          <Typography
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            No projects match your search criteria
          </Typography>
          <Button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setPriorityFilter("all");
              setDepartmentFilter("all");
              setProjectLeadFilter("all");
            }}
            sx={{
              color: "primary.main",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(245,158,11,0.1)"
                    : "#fffbeb",
              },
            }}
          >
            Clear filters
          </Button>
        </Paper>
      )}

      {/* Modals */}
      <AddProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchProjects())}
      />

      <EditProjectModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onSuccess={() => dispatch(fetchProjects())}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Yellow Banner */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            {/* Close Icon Button */}
            <IconButton
              onClick={handleDeleteCancel}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* SVG Icon */}
            <Box
              component="img"
              src="/src/assets/modals/delete-modal.svg"
              alt="Delete"
              sx={{
                width: 80,
                height: 80,
                objectFit: "contain",
                margin: "0 auto",
                display: "block",
                mb: 2,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "text.primary",
                textAlign: "center",
                mb: 2,
              }}
            >
              Delete Project
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to delete
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "primary.main",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{projectToDelete?.name}"
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              This project will be deleted permanently
            </Typography>
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  opacity: 0.6,
                },
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </motion.div>
      </Dialog>

      {/* Team Members Dialog */}
      <Dialog
        open={teamMembersDialogOpen}
        onClose={handleCloseTeamMembers}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Yellow Banner */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            {/* Close Icon Button */}
            <IconButton
              onClick={handleCloseTeamMembers}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* SVG Icon */}
            <Box
              component="img"
              src="/src/assets/modals/team-modal.svg"
              alt="Team Members"
              sx={{
                width: 80,
                height: 80,
                objectFit: "contain",
                margin: "0 auto",
                display: "block",
                mb: 2,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "text.primary",
                textAlign: "center",
                mb: 1,
              }}
            >
              Team Members
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 2,
              }}
            >
              {selectedProjectForTeam?.name}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Stack
              spacing={1.5}
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#f59e0b" : "#f59e0b",
                  borderRadius: "10px",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#d97706" : "#d97706",
                  },
                },
              }}
            >
              {/* Project Lead - Always shown first */}
              {selectedProjectForTeam?.project_manager_id && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1.5,
                    borderRadius: "12px",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.15)"
                        : "#fffbeb",
                    border: `1px solid ${
                      theme.palette.mode === "dark" ? "#f59e0b" : "#f59e0b"
                    }`,
                  }}
                >
                  <PersonIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        color: "primary.main",
                      }}
                    >
                      {employeeNameMap[
                        selectedProjectForTeam.project_manager_id
                      ] ||
                        `Employee ${selectedProjectForTeam.project_manager_id}`}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      ID: {selectedProjectForTeam.project_manager_id} • Project
                      Lead
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Other Team Members */}
              {selectedProjectForTeam?.team_members &&
                selectedProjectForTeam.team_members.length > 0 && (
                  <>
                    {selectedProjectForTeam.team_members
                      .filter(
                        (memberId) =>
                          memberId !==
                          selectedProjectForTeam.project_manager_id,
                      )
                      .map((memberId, idx) => {
                        const employeeName =
                          employeeNameMap[memberId] || `Employee ${memberId}`;
                        return (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              p: 1.5,
                              borderRadius: "12px",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(30,41,59,0.5)"
                                  : "#f9fafb",
                            }}
                          >
                            <PersonIcon
                              sx={{ color: "primary.main", fontSize: 20 }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                  color: "text.primary",
                                }}
                              >
                                {employeeName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "text.secondary",
                                }}
                              >
                                ID: {memberId}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                  </>
                )}

              {/* No team members message */}
              {(!selectedProjectForTeam?.team_members ||
                selectedProjectForTeam.team_members.length === 0 ||
                (selectedProjectForTeam.team_members.length === 1 &&
                  selectedProjectForTeam.team_members[0] ===
                    selectedProjectForTeam.project_manager_id)) && (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <PersonIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
                  <Typography
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    No additional team members assigned
                  </Typography>
                </Box>
              )}
            </Stack>

            <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
              <Button
                onClick={handleCloseTeamMembers}
                fullWidth
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "12px",
                  py: 1,
                  fontSize: "0.875rem",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "#ffffff",
                  boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </motion.div>
      </Dialog>
    </Box>
  );
  // }

  // if (role === "Company") {
  // return (
  //   <Box
  //     sx={{
  //       minHeight: "100vh",
  //       background:
  //         theme.palette.mode === "dark"
  //           ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
  //           : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
  //       p: 3,
  //     }}
  //   >
  //     {/* Header Section */}
  //     <Box sx={{ mb: 4 }}>
  //       <Stack
  //         direction={{ xs: "column", sm: "row" }}
  //         spacing={2}
  //         alignItems={{ sm: "center" }}
  //         justifyContent="space-between"
  //         sx={{ width: "100%" }}
  //       >
  //         <Stack
  //           direction="row"
  //           spacing={2}
  //           alignItems="center"
  //           sx={{ flex: 1 }}
  //         >
  //           <Box
  //             component="img"
  //             src={projectSvg}
  //             alt="Projects"
  //             sx={{
  //               width: { xs: 64, lg: 80 },
  //               height: { xs: 64, lg: 80 },
  //               objectFit: "contain",
  //             }}
  //           />
  //           <Box>
  //             <Typography
  //               variant="h4"
  //               component="h1"
  //               sx={{
  //                 fontWeight: "bold",
  //                 color: "text.primary",
  //               }}
  //             >
  //               Projects
  //             </Typography>
  //             <Typography
  //               variant="body2"
  //               sx={{
  //                 color: "text.secondary",
  //                 mt: 0.5,
  //               }}
  //             >
  //               Manage your organization projects
  //             </Typography>
  //           </Box>
  //         </Stack>

  //         <Box>
  //           <Button
  //             variant="contained"
  //             startIcon={<AddIcon />}
  //             onClick={() => setShowCreateModal(true)}
  //             sx={{
  //               background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  //               borderRadius: "10px",
  //               px: 2.5,
  //               py: 1,
  //               fontSize: "0.875rem",
  //               fontWeight: 500,
  //               textTransform: "none",
  //               minWidth: "auto",
  //               whiteSpace: "nowrap",
  //               boxShadow: "none",
  //               transition: "all 0.2s ease",
  //               "&:hover": {
  //                 background:
  //                   "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
  //                 boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
  //                 transform: "translateY(-1px)",
  //               },
  //               "&:active": {
  //                 transform: "translateY(0)",
  //               },
  //             }}
  //           >
  //             Add Project
  //           </Button>
  //         </Box>
  //       </Stack>
  //     </Box>

  //     {/* Search and Filter Section */}
  //     <Stack
  //       direction={{ xs: "column", lg: "row" }}
  //       spacing={2}
  //       alignItems={{ lg: "center" }}
  //       sx={{ mb: 3, flexWrap: "wrap" }}
  //     >
  //       <TextField
  //         placeholder="Search by project name, code or description..."
  //         value={searchTerm}
  //         onChange={(e) => setSearchTerm(e.target.value)}
  //         variant="outlined"
  //         size="small"
  //         sx={{
  //           flex: 2,
  //           minWidth: { xs: "100%", sm: 250 },
  //           "& .MuiOutlinedInput-root": {
  //             borderRadius: "12px",
  //             backgroundColor:
  //               theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //             "& fieldset": {
  //               borderColor:
  //                 theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
  //             },
  //             "&:hover fieldset": {
  //               borderColor: "#f59e0b",
  //             },
  //             "&.Mui-focused fieldset": {
  //               borderColor: "#f59e0b",
  //               borderWidth: "2px",
  //             },
  //           },
  //         }}
  //         InputProps={{
  //           startAdornment: (
  //             <InputAdornment position="start">
  //               <SearchIcon sx={{ color: "#9ca3af" }} />
  //             </InputAdornment>
  //           ),
  //         }}
  //       />

  //       <TextField
  //         select
  //         label="Department"
  //         value={departmentFilter}
  //         onChange={(e) => setDepartmentFilter(e.target.value)}
  //         size="small"
  //         sx={{
  //           minWidth: { xs: "100%", sm: 180 },
  //           "& .MuiOutlinedInput-root": {
  //             borderRadius: "12px",
  //             backgroundColor:
  //               theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //             "& fieldset": {
  //               borderColor:
  //                 theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
  //             },
  //             "&:hover fieldset": {
  //               borderColor: "#f59e0b",
  //             },
  //             "&.Mui-focused fieldset": {
  //               borderColor: "#f59e0b",
  //               borderWidth: "2px",
  //             },
  //           },
  //           "& .MuiInputLabel-root": {
  //             color: "text.secondary",
  //           },
  //           "& .MuiInputLabel-root.Mui-focused": {
  //             color: "primary.main",
  //           },
  //         }}
  //         SelectProps={{
  //           MenuProps: {
  //             PaperProps: {
  //               sx: {
  //                 borderRadius: "12px",
  //                 backgroundColor:
  //                   theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //               },
  //             },
  //           },
  //         }}
  //       >
  //         <MenuItem value="all">All Departments</MenuItem>
  //         {departments.map((dept) => (
  //           <MenuItem key={dept.id} value={dept.id}>
  //             {dept.name} ({dept.code})
  //           </MenuItem>
  //         ))}
  //       </TextField>

  //       <TextField
  //         select
  //         label="Project Lead"
  //         value={projectLeadFilter}
  //         onChange={(e) => setProjectLeadFilter(e.target.value)}
  //         size="small"
  //         sx={{
  //           minWidth: { xs: "100%", sm: 180 },
  //           "& .MuiOutlinedInput-root": {
  //             borderRadius: "12px",
  //             backgroundColor:
  //               theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //             "& fieldset": {
  //               borderColor:
  //                 theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
  //             },
  //             "&:hover fieldset": {
  //               borderColor: "#f59e0b",
  //             },
  //             "&.Mui-focused fieldset": {
  //               borderColor: "#f59e0b",
  //               borderWidth: "2px",
  //             },
  //           },
  //           "& .MuiInputLabel-root": {
  //             color: "text.secondary",
  //           },
  //           "& .MuiInputLabel-root.Mui-focused": {
  //             color: "primary.main",
  //           },
  //         }}
  //         SelectProps={{
  //           MenuProps: {
  //             PaperProps: {
  //               sx: {
  //                 borderRadius: "12px",
  //                 backgroundColor:
  //                   theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //               },
  //             },
  //           },
  //         }}
  //       >
  //         <MenuItem value="all">All Project Leads</MenuItem>
  //         {employees.map((emp) => (
  //           <MenuItem key={emp.id} value={emp.id}>
  //             {emp.first_name} {emp.last_name} ({emp.employee_code})
  //           </MenuItem>
  //         ))}
  //       </TextField>
  //       <TextField
  //         select
  //         label="Status"
  //         value={statusFilter}
  //         onChange={(e) => setStatusFilter(e.target.value)}
  //         size="small"
  //         sx={{
  //           minWidth: { xs: "100%", sm: 150 },
  //           "& .MuiOutlinedInput-root": {
  //             borderRadius: "12px",
  //             backgroundColor:
  //               theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //             "& fieldset": {
  //               borderColor:
  //                 theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
  //             },
  //             "&:hover fieldset": {
  //               borderColor: "#f59e0b",
  //             },
  //             "&.Mui-focused fieldset": {
  //               borderColor: "#f59e0b",
  //               borderWidth: "2px",
  //             },
  //           },
  //           "& .MuiInputLabel-root": {
  //             color: "text.secondary",
  //           },
  //           "& .MuiInputLabel-root.Mui-focused": {
  //             color: "primary.main",
  //           },
  //         }}
  //         SelectProps={{
  //           MenuProps: {
  //             PaperProps: {
  //               sx: {
  //                 borderRadius: "12px",
  //                 backgroundColor:
  //                   theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //               },
  //             },
  //           },
  //         }}
  //       >
  //         <MenuItem value="all">All Status</MenuItem>
  //         <MenuItem value="planning">Planning</MenuItem>
  //         <MenuItem value="active">Active</MenuItem>
  //         <MenuItem value="on_hold">On Hold</MenuItem>
  //         <MenuItem value="completed">Completed</MenuItem>
  //         <MenuItem value="cancelled">Cancelled</MenuItem>
  //       </TextField>

  //       <TextField
  //         select
  //         label="Priority"
  //         value={priorityFilter}
  //         onChange={(e) => setPriorityFilter(e.target.value)}
  //         size="small"
  //         sx={{
  //           minWidth: { xs: "100%", sm: 150 },
  //           "& .MuiOutlinedInput-root": {
  //             borderRadius: "12px",
  //             backgroundColor:
  //               theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //             "& fieldset": {
  //               borderColor:
  //                 theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
  //             },
  //             "&:hover fieldset": {
  //               borderColor: "#f59e0b",
  //             },
  //             "&.Mui-focused fieldset": {
  //               borderColor: "#f59e0b",
  //               borderWidth: "2px",
  //             },
  //           },
  //           "& .MuiInputLabel-root": {
  //             color: "text.secondary",
  //           },
  //           "& .MuiInputLabel-root.Mui-focused": {
  //             color: "primary.main",
  //           },
  //         }}
  //         SelectProps={{
  //           MenuProps: {
  //             PaperProps: {
  //               sx: {
  //                 borderRadius: "12px",
  //                 backgroundColor:
  //                   theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //               },
  //             },
  //           },
  //         }}
  //       >
  //         <MenuItem value="all">All Priorities</MenuItem>
  //         <MenuItem value="urgent">Urgent</MenuItem>
  //         <MenuItem value="high">High</MenuItem>
  //         <MenuItem value="medium">Medium</MenuItem>
  //         <MenuItem value="low">Low</MenuItem>
  //       </TextField>

  //       {(departmentFilter !== "all" ||
  //         projectLeadFilter !== "all" ||
  //         statusFilter !== "all" ||
  //         priorityFilter !== "all" ||
  //         searchTerm !== "") && (
  //           <Button
  //             onClick={() => {
  //               setSearchTerm("");
  //               setStatusFilter("all");
  //               setPriorityFilter("all");
  //               setDepartmentFilter("all");
  //               setProjectLeadFilter("all");
  //             }}
  //             size="small"
  //             sx={{
  //               textTransform: "none",
  //               borderRadius: "8px",
  //               px: 2,
  //               color: "primary.main",
  //               borderColor: "#f59e0b",
  //               whiteSpace: "nowrap",
  //               "&:hover": {
  //                 borderColor: "#d97706",
  //                 backgroundColor: "rgba(245,158,11,0.1)",
  //               },
  //             }}
  //             variant="outlined"
  //           >
  //             Clear All Filters
  //           </Button>
  //         )}
  //     </Stack>

  //     {/* Loading State */}
  //     {loading ? (
  //       <Box
  //         sx={{
  //           display: "flex",
  //           justifyContent: "center",
  //           alignItems: "center",
  //           height: 400,
  //         }}
  //       >
  //         <Stack alignItems="center" spacing={2}>
  //           <CircularProgress
  //             size={48}
  //             sx={{
  //               color: "primary.main",
  //               "& .MuiCircularProgress-circle": {
  //                 strokeLinecap: "round",
  //               },
  //             }}
  //           />
  //           <Typography
  //             sx={{
  //               color: "text.secondary",
  //             }}
  //           >
  //             Loading projects...
  //           </Typography>
  //         </Stack>
  //       </Box>
  //     ) : (
  //       <Stack spacing={2}>
  //         {filteredProjects.length === 0 ? (
  //           <Box
  //             sx={{
  //               textAlign: "center",
  //               py: 6,
  //               borderRadius: "16px",
  //               border: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
  //                 }`,
  //             }}
  //           >
  //             <ProjectIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
  //             <Typography
  //               sx={{
  //                 color:
  //                   theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
  //               }}
  //             >
  //               No projects found. Click "Add Project" to get started.
  //             </Typography>
  //           </Box>
  //         ) : (
  //           filteredProjects.map((project, index) => {
  //             const priorityColor =
  //               project.priority === "urgent"
  //                 ? theme.palette.mode === "dark"
  //                   ? "#f472b6"
  //                   : "#db2777"
  //                 : project.priority === "high"
  //                   ? theme.palette.mode === "dark"
  //                     ? "#fb3c3c"
  //                     : "#ea0c0c"
  //                   : project.priority === "medium"
  //                     ? theme.palette.mode === "dark"
  //                       ? "#fbbf24"
  //                       : "#d97706"
  //                     : project.priority === "low"
  //                       ? theme.palette.mode === "dark"
  //                         ? "#4ade80"
  //                         : "#16a34a"
  //                       : theme.palette.mode === "dark"
  //                         ? "#9ca3af"
  //                         : "#6b7280";
  //             return (
  //               <Box
  //                 key={project.id}
  //                 component={motion.div}
  //                 initial={{ opacity: 0, x: -20 }}
  //                 animate={{ opacity: 1, x: 0 }}
  //                 transition={{ delay: index * 0.05 }}
  //                 sx={{
  //                   display: "flex",
  //                   flexWrap: "wrap",
  //                   borderRadius: "12px",
  //                   overflow: "hidden",
  //                   backgroundColor:
  //                     theme.palette.mode === "dark" ? "#111827" : "#ffffff",
  //                   border: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
  //                     }`,
  //                   borderLeft: `4px solid ${priorityColor}`,
  //                   boxShadow:
  //                     theme.palette.mode === "dark"
  //                       ? "0 4px 12px -2px rgba(0,0,0,0.3)"
  //                       : "0 4px 12px -2px rgba(0,0,0,0.05)",
  //                   "&:hover": {
  //                     backgroundColor:
  //                       theme.palette.mode === "dark"
  //                         ? "rgba(255,178,46,0.05)"
  //                         : "rgba(245,158,11,0.03)",
  //                   },
  //                 }}
  //               >
  //                 {/* ID / Tenant */}
  //                 <Box
  //                   onClick={() => handleProject(project)}
  //                   onMouseMove={handleNavigateGlow}
  //                   sx={{
  //                     width: 130,
  //                     p: 2,
  //                     display: "flex",
  //                     flexDirection: "column",
  //                     alignItems: "flex-start",
  //                     gap: 2,
  //                     cursor: "pointer",
  //                     transition: "all 0.2s ease",
  //                     ...navigateGlowSx,
  //                   }}
  //                 >
  //                   <Box
  //                     sx={{
  //                       backgroundColor: theme.palette.mode === "dark" ? "#2a2d3c" : "#f3f4f6",
  //                       borderRadius: "6px",
  //                       padding: "4px 10px",

  //                     }}
  //                   >
  //                     <Typography
  //                       sx={{
  //                         fontFamily: "monospace",
  //                         fontSize: "1rem",
  //                         fontWeight: 700,
  //                         color: priorityColor,
  //                       }}
  //                     >
  //                       #{project.id}
  //                     </Typography>
  //                   </Box>
  //                   <Typography
  //                     sx={{
  //                       fontSize: "0.9rem",
  //                       color:
  //                         theme.palette.mode === "dark"
  //                           ? "#6b7280"
  //                           : "#9ca3af",
  //                     }}
  //                   >
  //                     Tenant: {project.tenant_id}
  //                   </Typography>
  //                 </Box>

  //                 {/* Code / Name / Description / Priority / Department */}
  //                 <Box
  //                   onClick={() => handleProject(project)}
  //                   onMouseMove={handleNavigateGlow}
  //                   sx={{
  //                     flex: "1 1 200px",
  //                     p: 2,
  //                     minWidth: 140,
  //                     gap: 0.75,
  //                     display: "flex",
  //                     flexDirection: "column",
  //                     cursor: "pointer",
  //                     transition: "all 0.2s ease",
  //                     borderRight: "1px solid rgba(255,255,255,0.08)",
  //                     ...navigateGlowSx,
  //                   }}>
  //                   <Typography
  //                     sx={{
  //                       fontFamily: "monospace",
  //                       fontWeight: 700,
  //                       fontSize: "0.75rem",
  //                       color: "primary.main",
  //                       mb: 0.5,

  //                     }}
  //                   >
  //                     {project.code}
  //                   </Typography>
  //                   <Typography
  //                     sx={{
  //                       fontWeight: 700,
  //                       color:
  //                         theme.palette.mode === "dark"
  //                           ? "#ffffff"
  //                           : "#111827",
  //                       fontSize: "1rem",
  //                     }}
  //                   >
  //                     {project.name}
  //                   </Typography>
  //                   {project.description && (
  //                     <Typography
  //                       sx={{
  //                         fontSize: "0.8rem",
  //                         fontStyle: "italic",
  //                         color:
  //                           theme.palette.mode === "dark"
  //                             ? "#9ca3af"
  //                             : "#6b7280",
  //                         mb: 1,
  //                       }}
  //                     >
  //                       {project.description}
  //                     </Typography>
  //                   )}
  //                   <Box sx={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", mt: 1 }}>
  //                     <Chip
  //                       icon={
  //                         project.priority === "urgent" ? (
  //                           <WarningIcon sx={{ fontSize: 14 }} />
  //                         ) : project.priority === "high" ? (
  //                           <TrendingUpIcon sx={{ fontSize: 14 }} />
  //                         ) : project.priority === "medium" ? (
  //                           <TrendingFlatIcon sx={{ fontSize: 14 }} />
  //                         ) : project.priority === "low" ? (
  //                           <TrendingDownIcon sx={{ fontSize: 14 }} />
  //                         ) : undefined
  //                       }
  //                       label={project.priority?.toUpperCase() || "N/A"}
  //                       size="small"
  //                       variant="outlined"
  //                       sx={{
  //                         borderColor: priorityColor,
  //                         color: priorityColor,
  //                         fontWeight: "bold",
  //                         fontStyle: "italic",
  //                         fontSize: "0.7rem",
  //                         px: 1,
  //                         height: "28px",
  //                         "& .MuiChip-icon": {
  //                           color: "inherit",
  //                           fontSize: "14px",
  //                         },
  //                       }}
  //                     />
  //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                       <FolderIcon sx={{ fontSize: 14, color: "primary.main" }} />
  //                       <Typography
  //                         sx={{
  //                           fontSize: "0.8rem",
  //                           fontWeight: "bold",
  //                           color:
  //                             theme.palette.mode === "dark"
  //                               ? "#d1d5db"
  //                               : "#4b5563",
  //                         }}
  //                       >
  //                         {departmentMap[project.department_id] ||
  //                           `Department ${project.department_id}`}
  //                         {project.department_id &&
  //                           ` (ID ${project.department_id})`}
  //                       </Typography>
  //                     </Box>
  //                   </Box>
  //                 </Box>

  //                 {/* Project Lead / Timeline */}
  //                 <Box
  //                   sx={{
  //                     flex: "1 1 220px",
  //                     p: 2,
  //                     minWidth: 180,
  //                     borderRight: "1px solid rgba(255,255,255,0.08)",
  //                   }}
  //                 >
  //                   <Typography
  //                     sx={{
  //                       fontWeight: 700,
  //                       fontSize: "0.7rem",
  //                       color:
  //                         theme.palette.mode === "dark"
  //                           ? "#9ca3af"
  //                           : "#6b7280",
  //                       textTransform: "uppercase",
  //                       mb: 0.5,
  //                     }}
  //                   >
  //                     Project Lead
  //                   </Typography>
  //                   <Box
  //                     sx={{
  //                       display: "flex",
  //                       alignItems: "center",
  //                       gap: 1,
  //                       mb: 1.5,
  //                     }}
  //                   >
  //                     <PersonIcon sx={{ fontSize: 16, color: "primary.main" }} />
  //                     <Typography
  //                       sx={{
  //                         fontSize: "0.875rem",
  //                         fontWeight: "bold",
  //                         fontStyle: "italic",
  //                         color:
  //                           theme.palette.mode === "dark"
  //                             ? "#d1d5db"
  //                             : "#4b5563",
  //                       }}
  //                     >
  //                       {employeeMap[project.project_manager_id] ||
  //                         `Employee ${project.project_manager_id}`}
  //                       {project.project_manager_id &&
  //                         ` (ID ${project.project_manager_id})`}
  //                     </Typography>
  //                   </Box>
  //                   <Button
  //                     variant="contained"
  //                     size="small"
  //                     startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
  //                     onClick={() => handleViewTeamMembers(project)}
  //                     sx={{
  //                       textTransform: "none",
  //                       fontSize: "0.75rem",
  //                       fontWeight: 600,
  //                       backgroundColor: "#f59e0b",
  //                       color: "#ffffff",
  //                       borderRadius: "8px",
  //                       padding: "4px 12px",
  //                       minWidth: "150px",
  //                       minHeight: "32px",
  //                       boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  //                       transition: "all 0.2s ease",
  //                       mb: 2,
  //                       "&:hover": {
  //                         backgroundColor: "#d97706",
  //                         transform: "translateY(-1px)",
  //                         boxShadow: "0 4px 8px rgba(245,158,11,0.3)",
  //                       },
  //                       "&:active": {
  //                         transform: "translateY(1px)",
  //                         boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  //                       },
  //                     }}
  //                   >
  //                     View Team
  //                   </Button>
  //                   <Typography
  //                     sx={{
  //                       fontWeight: 700,
  //                       fontSize: "0.7rem",
  //                       color:
  //                         theme.palette.mode === "dark"
  //                           ? "#9ca3af"
  //                           : "#6b7280",
  //                       textTransform: "uppercase",
  //                       mb: 0.5,
  //                     }}
  //                   >
  //                     Timeline
  //                   </Typography>
  //                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                     <EventIcon
  //                       sx={{
  //                         fontSize: 14,
  //                         color:
  //                           theme.palette.mode === "dark"
  //                             ? "#d1d5db"
  //                             : "#4b5563",
  //                       }}
  //                     />
  //                     <Typography
  //                       sx={{
  //                         fontSize: "0.75rem",
  //                         color:
  //                           theme.palette.mode === "dark"
  //                             ? "#d1d5db"
  //                             : "#4b5563",
  //                         fontFamily: "monospace",
  //                         whiteSpace: "nowrap",
  //                       }}
  //                     >
  //                       {formatDateShort(project.start_date)} -{" "}
  //                       {formatDateShort(project.end_date)}
  //                     </Typography>
  //                   </Box>
  //                 </Box>

  //                 {/* Status / Budget / Tags */}
  //                 <Box sx={{ flex: "1 1 200px", p: 2, minWidth: 180 }}>
  //                   <Typography
  //                     sx={{
  //                       fontWeight: 700,
  //                       fontSize: "0.7rem",
  //                       color:
  //                         theme.palette.mode === "dark"
  //                           ? "#9ca3af"
  //                           : "#6b7280",
  //                       textTransform: "uppercase",
  //                       mb: 0.5,
  //                     }}
  //                   >
  //                     Status
  //                   </Typography>
  //                   <Chip
  //                     icon={
  //                       project.status === "planning" ? (
  //                         <EditNoteIcon sx={{ fontSize: 14 }} />
  //                       ) : project.status === "active" ? (
  //                         <RadioButtonCheckedIcon sx={{ fontSize: 14 }} />
  //                       ) : project.status === "on_hold" ? (
  //                         <RadioButtonCheckedIcon sx={{ fontSize: 14 }} />
  //                       ) : project.status === "completed" ? (
  //                         <CheckCircleIcon sx={{ fontSize: 14 }} />
  //                       ) : project.status === "cancelled" ? (
  //                         <CancelIcon sx={{ fontSize: 14 }} />
  //                       ) : undefined
  //                     }
  //                     label={
  //                       project.status === "on_hold"
  //                         ? "On Hold"
  //                         : project.status === "planning"
  //                           ? "Planning"
  //                           : project.status?.charAt(0).toUpperCase() +
  //                           project.status?.slice(1) || "N/A"
  //                     }
  //                     size="small"
  //                     sx={{
  //                       backgroundColor:
  //                         project.status === "planning"
  //                           ? "#fef3c7"
  //                           : project.status === "active"
  //                             ? "#d1fae5"
  //                             : project.status === "on_hold"
  //                               ? "#fef3c7"
  //                               : project.status === "completed"
  //                                 ? "#dbeafe"
  //                                 : project.status === "cancelled"
  //                                   ? "#fee2e2"
  //                                   : "#e5e7eb",
  //                       color:
  //                         project.status === "planning"
  //                           ? "#92400e"
  //                           : project.status === "active"
  //                             ? "#065f46"
  //                             : project.status === "on_hold"
  //                               ? "#92400e"
  //                               : project.status === "completed"
  //                                 ? "#1e40af"
  //                                 : project.status === "cancelled"
  //                                   ? "#991b1b"
  //                                   : "#374151",
  //                       fontWeight: 500,
  //                       fontSize: "0.75rem",
  //                       height: "24px",
  //                       mb: 1.5,
  //                       "& .MuiChip-icon": {
  //                         color: "inherit",
  //                         fontSize: "14px",
  //                       },
  //                     }}
  //                   />
  //                   <Box
  //                     sx={{
  //                       mt: 2,
  //                       pt: 2,
  //                       borderTop: "1px solid rgba(255,255,255,0.08)",
  //                       display: "flex",
  //                       justifyContent: "space-between",
  //                       alignItems: "flex-start",
  //                       gap: 4,
  //                     }}
  //                   >
  //                     <Box>
  //                       <Typography
  //                         sx={{
  //                           fontWeight: 700,
  //                           fontSize: "0.7rem",
  //                           color:
  //                             theme.palette.mode === "dark"
  //                               ? "#9ca3af"
  //                               : "#6b7280",
  //                           textTransform: "uppercase",
  //                           mb: 0.5,
  //                         }}
  //                       >
  //                         Budget
  //                       </Typography>

  //                       <Typography
  //                         sx={{
  //                           fontSize: "0.875rem",
  //                           fontWeight: 600,
  //                           color:
  //                             theme.palette.mode === "dark"
  //                               ? "#fbbf24"
  //                               : "#d97706",
  //                           mb: 1.5,
  //                         }}
  //                       >
  //                         {formatBudget(project.budget)}
  //                       </Typography>
  //                     </Box>

  //                     <Box>
  //                       <Typography
  //                         sx={{
  //                           fontWeight: 700,
  //                           fontSize: "0.7rem",
  //                           minWidth: 90,
  //                           display: "flex",
  //                           alignItems: "flex-start",
  //                           flexDirection: "column",
  //                           color:
  //                             theme.palette.mode === "dark"
  //                               ? "#9ca3af"
  //                               : "#6b7280",
  //                           textTransform: "uppercase",
  //                           mb: 0.5,
  //                         }}
  //                       >
  //                         Tags
  //                       </Typography>
  //                       <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
  //                         {project.tags && project.tags.length > 0 ? (
  //                           project.tags.map((tag, idx) => (
  //                             <Chip
  //                               key={idx}
  //                               label={tag}
  //                               size="small"
  //                               icon={<LocalOfferIcon />}
  //                               sx={{
  //                                 backgroundColor: "transparent",
  //                                 color:
  //                                   theme.palette.mode === "dark"
  //                                     ? "#4bb0de"
  //                                     : "#0284c7",
  //                                 fontStyle: "italic",
  //                                 height: "22px",
  //                                 fontSize: "0.7rem",
  //                                 width: "fit-content",
  //                                 margin: "-2px 0",
  //                                 "& .MuiChip-icon": {
  //                                   color:
  //                                     theme.palette.mode === "dark"
  //                                       ? "#4bb0de"
  //                                       : "#0284c7",
  //                                   fontSize: "12px",
  //                                   margin: "0 2px 0 -4px",
  //                                 },
  //                                 "& .MuiChip-label": {
  //                                   fontStyle: "italic",
  //                                   padding: "0 4px",
  //                                 },
  //                               }}
  //                             />
  //                           ))
  //                         ) : (
  //                           <Typography
  //                             sx={{
  //                               fontSize: "0.75rem",
  //                               color:
  //                                 theme.palette.mode === "dark"
  //                                   ? "#6b7280"
  //                                   : "#9ca3af",
  //                             }}
  //                           >
  //                             (No tags)
  //                           </Typography>
  //                         )}
  //                       </Stack>
  //                     </Box>
  //                   </Box>
  //                 </Box>

  //                 {/* Actions */}
  //                 <Box
  //                   sx={{
  //                     width: 90,
  //                     p: 2,
  //                     display: "flex",
  //                     flexDirection: "column",
  //                     alignItems: "center",
  //                     borderLeft: "1px solid rgba(255,255,255,0.08)",
  //                     gap: 0.75,
  //                     justifyContent: "flex-start",
  //                   }}
  //                 >
  //                   <Typography
  //                     sx={{
  //                       fontWeight: 700,
  //                       fontSize: "0.7rem",
  //                       color:
  //                         theme.palette.mode === "dark"
  //                           ? "#9ca3af"
  //                           : "#6b7280",
  //                       textTransform: "uppercase",
  //                       mb: 0.5,
  //                     }}
  //                   >
  //                     Actions
  //                   </Typography>
  //                   <IconButton
  //                     onClick={() => {
  //                       setSelectedProject(project);
  //                       setShowEditModal(true);
  //                     }}
  //                     size="small"
  //                     sx={{
  //                       color: "primary.main",
  //                       "&:hover": {
  //                         backgroundColor:
  //                           theme.palette.mode === "dark"
  //                             ? "rgba(245,158,11,0.2)"
  //                             : "#fff5ce",
  //                       },
  //                     }}
  //                   >
  //                     <EditIcon />
  //                   </IconButton>
  //                   <Box
  //                     sx={{
  //                       width: "100%",
  //                       height: "1px",
  //                       backgroundColor: "rgba(255,255,255,0.08)",
  //                       my: 1,
  //                     }}
  //                   />
  //                   <IconButton
  //                     onClick={() => handleDeleteClick(project)}
  //                     disabled={deleting}
  //                     size="small"
  //                     sx={{
  //                       color: "#ef4444",
  //                       "&:hover": {
  //                         backgroundColor:
  //                           theme.palette.mode === "dark"
  //                             ? "rgba(239,68,68,0.2)"
  //                             : "#fee2e2",
  //                       },
  //                     }}
  //                   >
  //                     <DeleteIcon />
  //                   </IconButton>
  //                 </Box>
  //               </Box>
  //             );
  //           })
  //         )}
  //       </Stack>
  //     )}

  //     {/* Project Count Summary */}
  //     {!loading && projects.length > 0 && (
  //       <Stack
  //         direction="row"
  //         justifyContent="space-between"
  //         sx={{
  //           mt: 2,
  //           gap: 2,
  //           fontSize: "0.875rem",
  //           color: "text.secondary",
  //         }}
  //       >
  //         <Typography variant="body2">
  //           Total Projects: {projects.length}
  //         </Typography>
  //         <Typography variant="body2">
  //           Showing: {filteredProjects.length} of {projects.length}
  //         </Typography>
  //       </Stack>
  //     )}

  //     {/* Empty State for No Search Results */}
  //     {!loading && filteredProjects.length === 0 && projects.length > 0 && (
  //       <Paper
  //         sx={{
  //           mt: 4,
  //           p: 4,
  //           textAlign: "center",
  //           borderRadius: "16px",
  //           backgroundColor:
  //             theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
  //         }}
  //       >
  //         <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
  //         <Typography
  //           sx={{
  //             color: "text.secondary",
  //             mb: 2,
  //           }}
  //         >
  //           No projects match your search criteria
  //         </Typography>
  //         <Button
  //           onClick={() => {
  //             setSearchTerm("");
  //             setStatusFilter("all");
  //             setPriorityFilter("all");
  //             setDepartmentFilter("all");
  //             setProjectLeadFilter("all");
  //           }}
  //           sx={{
  //             color: "primary.main",
  //             textTransform: "none",
  //             "&:hover": {
  //               backgroundColor:
  //                 theme.palette.mode === "dark"
  //                   ? "rgba(245,158,11,0.1)"
  //                   : "#fffbeb",
  //             },
  //           }}
  //         >
  //           Clear filters
  //         </Button>
  //       </Paper>
  //     )}

  //     {/* Modals */}
  //     <AddProjectModal
  //       isOpen={showCreateModal}
  //       onClose={() => setShowCreateModal(false)}
  //       onSuccess={() => dispatch(fetchProjects())}
  //     />

  //     <EditProjectModal
  //       isOpen={showEditModal}
  //       onClose={() => {
  //         setShowEditModal(false);
  //         setSelectedProject(null);
  //       }}
  //       project={selectedProject}
  //       onSuccess={() => dispatch(fetchProjects())}
  //     />

  //     {/* Delete Confirmation Dialog */}
  //     <Dialog
  //       open={deleteDialogOpen}
  //       onClose={handleDeleteCancel}
  //       maxWidth="xs"
  //       fullWidth
  //       TransitionComponent={motion.div}
  //       TransitionProps={{
  //         timeout: 300,
  //         style: { overflow: "visible" },
  //       }}
  //       sx={{
  //         "& .MuiDialog-paper": {
  //           borderRadius: "20px",
  //           overflow: "hidden",
  //           backgroundColor:
  //             theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
  //           maxWidth: "400px",
  //           margin: "16px",
  //           boxShadow: theme.shadows[24],
  //           position: "relative",
  //         },
  //       }}
  //       slotProps={{
  //         backdrop: {
  //           sx: {
  //             backgroundColor: "rgba(0, 0, 0, 0.6)",
  //             backdropFilter: "blur(4px)",
  //           },
  //         },
  //       }}
  //     >
  //       <motion.div
  //         initial={{ x: "100%", opacity: 0 }}
  //         animate={{ x: 0, opacity: 1 }}
  //         exit={{ x: "100%", opacity: 0 }}
  //         transition={{ type: "spring", damping: 25, stiffness: 200 }}
  //       >
  //         {/* Yellow Banner */}
  //         <Box
  //           sx={{
  //             background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  //             height: "6px",
  //           }}
  //         />

  //         <Box sx={{ p: 3, position: "relative" }}>
  //           {/* Close Icon Button */}
  //           <IconButton
  //             onClick={handleDeleteCancel}
  //             sx={{
  //               position: "absolute",
  //               top: 16,
  //               right: 16,
  //               width: 32,
  //               height: 32,
  //               borderRadius: "8px",
  //               color: "text.secondary",
  //               "&:hover": {
  //                 backgroundColor: "#f59e0b",
  //                 color: "#ffffff",
  //               },
  //             }}
  //           >
  //             <CloseIcon sx={{ fontSize: 18 }} />
  //           </IconButton>

  //           {/* SVG Icon */}
  //           <Box
  //             component="img"
  //             src="/src/assets/modals/delete-modal.svg"
  //             alt="Delete"
  //             sx={{
  //               width: 80,
  //               height: 80,
  //               objectFit: "contain",
  //               margin: "0 auto",
  //               display: "block",
  //               mb: 2,
  //             }}
  //             onError={(e) => {
  //               e.target.style.display = "none";
  //             }}
  //           />

  //           <Typography
  //             sx={{
  //               fontWeight: 700,
  //               fontSize: "1.25rem",
  //               color: "text.primary",
  //               textAlign: "center",
  //               mb: 2,
  //             }}
  //           >
  //             Delete Project
  //           </Typography>

  //           <Typography
  //             sx={{
  //               color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
  //               fontSize: "0.95rem",
  //               lineHeight: 1.5,
  //               textAlign: "center",
  //               mb: 1,
  //             }}
  //           >
  //             Are you sure you want to delete
  //           </Typography>

  //           <Typography
  //             sx={{
  //               fontWeight: 600,
  //               color: "primary.main",
  //               fontSize: "1rem",
  //               textAlign: "center",
  //               mb: 2,
  //               wordBreak: "break-word",
  //             }}
  //           >
  //             "{projectToDelete?.name}"
  //           </Typography>

  //           <Typography
  //             sx={{
  //               color: "text.secondary",
  //               fontSize: "0.85rem",
  //               lineHeight: 1.4,
  //               textAlign: "center",
  //             }}
  //           >
  //             This project will be deleted permanently
  //           </Typography>
  //         </Box>

  //         <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
  //           <Button
  //             onClick={handleDeleteCancel}
  //             disabled={deleting}
  //             fullWidth
  //             sx={{
  //               textTransform: "none",
  //               fontWeight: 600,
  //               borderRadius: "12px",
  //               py: 1,
  //               fontSize: "0.875rem",
  //               color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
  //               backgroundColor:
  //                 theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
  //               border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
  //                 }`,
  //               "&:hover": {
  //                 backgroundColor:
  //                   theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
  //               },
  //             }}
  //           >
  //             Cancel
  //           </Button>
  //           <Button
  //             onClick={handleDeleteConfirm}
  //             disabled={deleting}
  //             fullWidth
  //             variant="contained"
  //             sx={{
  //               textTransform: "none",
  //               fontWeight: 600,
  //               borderRadius: "12px",
  //               py: 1,
  //               fontSize: "0.875rem",
  //               background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  //               color: "#ffffff",
  //               boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
  //               "&:hover": {
  //                 background:
  //                   "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
  //                 boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
  //                 transform: "translateY(-1px)",
  //               },
  //               "&:active": {
  //                 transform: "translateY(0)",
  //               },
  //               "&:disabled": {
  //                 background:
  //                   "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  //                 opacity: 0.6,
  //               },
  //             }}
  //           >
  //             {deleting ? (
  //               <CircularProgress size={20} sx={{ color: "#ffffff" }} />
  //             ) : (
  //               "Delete"
  //             )}
  //           </Button>
  //         </Box>
  //       </motion.div>
  //     </Dialog>

  //     {/* Team Members Dialog */}
  //     <Dialog
  //       open={teamMembersDialogOpen}
  //       onClose={handleCloseTeamMembers}
  //       maxWidth="xs"
  //       fullWidth
  //       TransitionComponent={motion.div}
  //       TransitionProps={{
  //         timeout: 300,
  //         style: { overflow: "visible" },
  //       }}
  //       sx={{
  //         "& .MuiDialog-paper": {
  //           borderRadius: "20px",
  //           overflow: "hidden",
  //           backgroundColor:
  //             theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
  //           maxWidth: "400px",
  //           margin: "16px",
  //           boxShadow: theme.shadows[24],
  //           position: "relative",
  //         },
  //       }}
  //       slotProps={{
  //         backdrop: {
  //           sx: {
  //             backgroundColor: "rgba(0, 0, 0, 0.6)",
  //             backdropFilter: "blur(4px)",
  //           },
  //         },
  //       }}
  //     >
  //       <motion.div
  //         initial={{ x: "100%", opacity: 0 }}
  //         animate={{ x: 0, opacity: 1 }}
  //         exit={{ x: "100%", opacity: 0 }}
  //         transition={{ type: "spring", damping: 25, stiffness: 200 }}
  //       >
  //         {/* Yellow Banner */}
  //         <Box
  //           sx={{
  //             background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  //             height: "6px",
  //           }}
  //         />

  //         <Box sx={{ p: 3, position: "relative" }}>
  //           {/* Close Icon Button */}
  //           <IconButton
  //             onClick={handleCloseTeamMembers}
  //             sx={{
  //               position: "absolute",
  //               top: 16,
  //               right: 16,
  //               width: 32,
  //               height: 32,
  //               borderRadius: "8px",
  //               color: "text.secondary",
  //               "&:hover": {
  //                 backgroundColor: "#f59e0b",
  //                 color: "#ffffff",
  //               },
  //             }}
  //           >
  //             <CloseIcon sx={{ fontSize: 18 }} />
  //           </IconButton>

  //           {/* SVG Icon */}
  //           <Box
  //             component="img"
  //             src="/src/assets/modals/team-modal.svg"
  //             alt="Team Members"
  //             sx={{
  //               width: 80,
  //               height: 80,
  //               objectFit: "contain",
  //               margin: "0 auto",
  //               display: "block",
  //               mb: 2,
  //             }}
  //             onError={(e) => {
  //               e.target.style.display = "none";
  //             }}
  //           />

  //           <Typography
  //             sx={{
  //               fontWeight: 700,
  //               fontSize: "1.25rem",
  //               color: "text.primary",
  //               textAlign: "center",
  //               mb: 1,
  //             }}
  //           >
  //             Team Members
  //           </Typography>

  //           <Typography
  //             sx={{
  //               color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
  //               fontSize: "0.95rem",
  //               lineHeight: 1.5,
  //               textAlign: "center",
  //               mb: 2,
  //             }}
  //           >
  //             {selectedProjectForTeam?.name}
  //           </Typography>

  //           <Divider sx={{ my: 2 }} />

  //           <Stack
  //             spacing={1.5}
  //             sx={{
  //               maxHeight: "300px",
  //               overflowY: "auto",
  //               "&::-webkit-scrollbar": {
  //                 width: "6px",
  //               },
  //               "&::-webkit-scrollbar-track": {
  //                 backgroundColor:
  //                   theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
  //                 borderRadius: "10px",
  //               },
  //               "&::-webkit-scrollbar-thumb": {
  //                 backgroundColor:
  //                   theme.palette.mode === "dark" ? "#f59e0b" : "#f59e0b",
  //                 borderRadius: "10px",
  //                 "&:hover": {
  //                   backgroundColor:
  //                     theme.palette.mode === "dark" ? "#d97706" : "#d97706",
  //                 },
  //               },
  //             }}
  //           >
  //             {/* Project Lead - Always shown first */}
  //             {selectedProjectForTeam?.project_manager_id && (
  //               <Box
  //                 sx={{
  //                   display: "flex",
  //                   alignItems: "center",
  //                   gap: 2,
  //                   p: 1.5,
  //                   borderRadius: "12px",
  //                   backgroundColor:
  //                     theme.palette.mode === "dark"
  //                       ? "rgba(245,158,11,0.15)"
  //                       : "#fffbeb",
  //                   border: `1px solid ${theme.palette.mode === "dark" ? "#f59e0b" : "#f59e0b"
  //                     }`,
  //                 }}
  //               >
  //                 <PersonIcon sx={{ color: "primary.main", fontSize: 20 }} />
  //                 <Box>
  //                   <Typography
  //                     sx={{
  //                       fontWeight: 700,
  //                       fontSize: "0.9rem",
  //                       color: "primary.main",
  //                     }}
  //                   >
  //                     {employeeNameMap[
  //                       selectedProjectForTeam.project_manager_id
  //                     ] ||
  //                       `Employee ${selectedProjectForTeam.project_manager_id}`}
  //                   </Typography>
  //                   <Typography
  //                     sx={{
  //                       fontSize: "0.7rem",
  //                       color:
  //                         theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
  //                     }}
  //                   >
  //                     ID: {selectedProjectForTeam.project_manager_id} • Project
  //                     Lead
  //                   </Typography>
  //                 </Box>
  //               </Box>
  //             )}

  //             {/* Other Team Members */}
  //             {selectedProjectForTeam?.team_members &&
  //               selectedProjectForTeam.team_members.length > 0 && (
  //                 <>
  //                   {selectedProjectForTeam.team_members
  //                     .filter(
  //                       (memberId) =>
  //                         memberId !==
  //                         selectedProjectForTeam.project_manager_id,
  //                     )
  //                     .map((memberId, idx) => {
  //                       const employeeName =
  //                         employeeNameMap[memberId] || `Employee ${memberId}`;
  //                       return (
  //                         <Box
  //                           key={idx}
  //                           sx={{
  //                             display: "flex",
  //                             alignItems: "center",
  //                             gap: 2,
  //                             p: 1.5,
  //                             borderRadius: "12px",
  //                             backgroundColor:
  //                               theme.palette.mode === "dark"
  //                                 ? "rgba(30,41,59,0.5)"
  //                                 : "#f9fafb",
  //                           }}
  //                         >
  //                           <PersonIcon
  //                             sx={{ color: "primary.main", fontSize: 20 }}
  //                           />
  //                           <Box>
  //                             <Typography
  //                               sx={{
  //                                 fontWeight: 500,
  //                                 fontSize: "0.9rem",
  //                                 color:
  //                                   theme.palette.mode === "dark"
  //                                     ? "#ffffff"
  //                                     : "#111827",
  //                               }}
  //                             >
  //                               {employeeName}
  //                             </Typography>
  //                             <Typography
  //                               sx={{
  //                                 fontSize: "0.7rem",
  //                                 color:
  //                                   theme.palette.mode === "dark"
  //                                     ? "#9ca3af"
  //                                     : "#6b7280",
  //                               }}
  //                             >
  //                               ID: {memberId}
  //                             </Typography>
  //                           </Box>
  //                         </Box>
  //                       );
  //                     })}
  //                 </>
  //               )}

  //             {/* No team members message */}
  //             {(!selectedProjectForTeam?.team_members ||
  //               selectedProjectForTeam.team_members.length === 0 ||
  //               (selectedProjectForTeam.team_members.length === 1 &&
  //                 selectedProjectForTeam.team_members[0] ===
  //                 selectedProjectForTeam.project_manager_id)) && (
  //                 <Box sx={{ textAlign: "center", py: 3 }}>
  //                   <PersonIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
  //                   <Typography
  //                     sx={{
  //                       color:
  //                         theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
  //                     }}
  //                   >
  //                     No additional team members assigned
  //                   </Typography>
  //                 </Box>
  //               )}
  //           </Stack>

  //           <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
  //             <Button
  //               onClick={handleCloseTeamMembers}
  //               fullWidth
  //               sx={{
  //                 textTransform: "none",
  //                 fontWeight: 600,
  //                 borderRadius: "12px",
  //                 py: 1,
  //                 fontSize: "0.875rem",
  //                 background:
  //                   "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  //                 color: "#ffffff",
  //                 boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
  //                 "&:hover": {
  //                   background:
  //                     "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
  //                   boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
  //                   transform: "translateY(-1px)",
  //                 },
  //                 "&:active": {
  //                   transform: "translateY(0)",
  //                 },
  //               }}
  //             >
  //               Close
  //             </Button>
  //           </Box>
  //         </Box>
  //       </motion.div>
  //     </Dialog>
  //   </Box>
  // );
  // }
};

export default Project;
